import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { Download, Loader2, Search, ExternalLink, Lock, ArrowRight, ArrowUp, ArrowDown, Filter, Printer, FileText } from "lucide-react";
import logoUrl from "@/assets/apex-av-new-logo-optimized.webp";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { searchSoldHomes, type SoldHome } from "@/lib/soldHomes.functions";
import { verifyEstimatorPassword } from "@/lib/estimator.functions";
import { getStoredPassword, setStoredPassword } from "@/lib/estimator-session";

export const Route = createFileRoute("/sold-homes")({
  component: SoldHomesPage,
  head: () => ({
    meta: [
      { title: "Recently Sold Homes Search by Zip Code | Apex AV" },
      {
        name: "description",
        content:
          "Find recently sold homes near any US zip code. Search by radius and export the results to CSV.",
      },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Sold Homes Search | Apex AV" },
      { property: "og:description", content: "Password-protected sold home search and address exports for Apex AV." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

const RADIUS_OPTIONS = [1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];

type SortKey =
  | "address"
  | "city"
  | "state"
  | "zip"
  | "price"
  | "soldDate"
  | "beds"
  | "baths"
  | "sqft"
  | "lotSize"
  | "yearBuilt"
  | "propertyType";

function fmtMoney(n?: number) {
  if (typeof n !== "number" || !isFinite(n)) return "";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function parseSoldDate(d?: string): number | null {
  if (!d) return null;
  const t = Date.parse(d);
  return isNaN(t) ? null : t;
}

function toCsv(rows: SoldHome[]): string {
  const cols: (keyof SoldHome)[] = [
    "address", "city", "state", "zip", "price", "soldDate",
    "beds", "baths", "sqft", "lotSize", "yearBuilt", "propertyType", "url",
  ];
  const esc = (v: unknown) => {
    if (v === undefined || v === null) return "";
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [cols.join(",")];
  for (const r of rows) lines.push(cols.map((c) => esc(r[c])).join(","));
  return lines.join("\n");
}

function SoldHomesPage() {
  const [unlocked, setUnlocked] = useState(false);
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      sessionStorage.getItem("apex_estimator_unlocked") === "1" &&
      getStoredPassword()
    ) {
      setUnlocked(true);
    }
  }, []);
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      {unlocked ? <SoldHomes /> : <PasswordGate onUnlock={() => setUnlocked(true)} />}
      <SiteFooter />
    </div>
  );
}

function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const verify = useServerFn(verifyEstimatorPassword);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await verify({ data: { password } });
      if (res.ok) {
        setStoredPassword(password);
        onUnlock();
      } else setError("Incorrect password.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="container mx-auto px-6 py-24 flex items-center justify-center min-h-[70vh]">
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-2xl border border-border bg-card p-10 shadow-card"
      >
        <div className="h-12 w-12 rounded-lg bg-gradient-brand flex items-center justify-center shadow-glow">
          <Lock className="h-6 w-6 text-brand-foreground" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-bold">Sold homes search</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Enter the password provided by Apex AV to access this tool.
        </p>
        <label className="block mt-8 text-sm font-medium">Password</label>
        <input
          type="password"
          aria-label="Password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 w-full rounded-md border border-border bg-background px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand"
        />
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        <button
          type="submit"
          disabled={loading || !password}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow disabled:opacity-50"
        >
          {loading ? "Verifying…" : "Unlock"} <ArrowRight className="h-4 w-4" />
        </button>
      </form>
    </section>
  );
}

function SoldHomes() {
  const searchFn = useServerFn(searchSoldHomes);
  const [zipcode, setZipcode] = useState("");
  const [radius, setRadius] = useState(5);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(false);
  const [homes, setHomes] = useState<SoldHome[]>([]);
  const [searched, setSearched] = useState(false);
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourceName, setSourceName] = useState("");

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>("soldDate");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [returnAddr, setReturnAddr] = useState({
    name: "Apex AV",
    line1: "2921 W 38th AVE STE 169",
    line2: "Denver, CO 80211",
  });
  const [customLogo, setCustomLogo] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  type Preset = { id: string; name: string; addr: { name: string; line1: string; line2: string }; logo: string | null };
  const [presets, setPresets] = useState<Preset[]>([]);
  const [activePresetId, setActivePresetId] = useState<string>("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const a = localStorage.getItem("apex_envelope_return_addr");
      if (a) setReturnAddr(JSON.parse(a));
      const l = localStorage.getItem("apex_envelope_logo");
      if (l) setCustomLogo(l);
      const p = localStorage.getItem("apex_envelope_presets");
      if (p) setPresets(JSON.parse(p));
      const ap = localStorage.getItem("apex_envelope_active_preset");
      if (ap) setActivePresetId(ap);
    } catch {}
  }, []);

  function persistPresets(next: Preset[]) {
    setPresets(next);
    try { localStorage.setItem("apex_envelope_presets", JSON.stringify(next)); } catch {}
  }

  function saveReturnAddress() {
    try {
      localStorage.setItem("apex_envelope_return_addr", JSON.stringify(returnAddr));
      if (customLogo) localStorage.setItem("apex_envelope_logo", customLogo);
      else localStorage.removeItem("apex_envelope_logo");
      setSavedNotice("Saved");
      setTimeout(() => setSavedNotice(null), 2000);
      toast.success("Return address saved as default");
    } catch {
      toast.error("Could not save");
    }
  }

  function selectPreset(id: string) {
    setActivePresetId(id);
    try { localStorage.setItem("apex_envelope_active_preset", id); } catch {}
    if (!id) return;
    const p = presets.find((x) => x.id === id);
    if (p) {
      setReturnAddr(p.addr);
      setCustomLogo(p.logo);
    }
  }

  function saveAsPreset() {
    const name = window.prompt("Preset name (e.g. 'Denver office')")?.trim();
    if (!name) return;
    const id = `p_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const next = [...presets, { id, name, addr: { ...returnAddr }, logo: customLogo }];
    persistPresets(next);
    selectPreset(id);
    toast.success(`Preset "${name}" saved`);
  }

  function updateCurrentPreset() {
    if (!activePresetId) return;
    const next = presets.map((p) =>
      p.id === activePresetId ? { ...p, addr: { ...returnAddr }, logo: customLogo } : p,
    );
    persistPresets(next);
    toast.success("Preset updated");
  }

  function deleteCurrentPreset() {
    if (!activePresetId) return;
    const p = presets.find((x) => x.id === activePresetId);
    if (!p) return;
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    persistPresets(presets.filter((x) => x.id !== activePresetId));
    selectPreset("");
    toast.success("Preset deleted");
  }

  function onLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCustomLogo(String(reader.result));
    reader.readAsDataURL(f);
  }

  function resetLogo() {
    setCustomLogo(null);
    try { localStorage.removeItem("apex_envelope_logo"); } catch {}
  }

  const [appliedFilters, setAppliedFilters] = useState({
    dateFrom: "",
    dateTo: "",
    minPrice: "",
  });

  const fromTs = appliedFilters.dateFrom ? new Date(appliedFilters.dateFrom).getTime() : null;
  const toTs = appliedFilters.dateTo ? new Date(appliedFilters.dateTo).getTime() + 86_400_000 - 1 : null;
  const minPriceRaw = appliedFilters.minPrice ? Number(appliedFilters.minPrice) : null;
  const minPriceNum = minPriceRaw !== null && !isNaN(minPriceRaw) ? minPriceRaw : null;

  const filteredHomes = useMemo(() => {
    return homes.filter((h) => {
      if (minPriceNum !== null) {
        const p =
          typeof h.price === "number"
            ? h.price
            : Number(String(h.price ?? "").replace(/[^0-9.]/g, ""));
        if (!isFinite(p) || p <= 0 || p < minPriceNum) return false;
      }
      const t = parseSoldDate(h.soldDate);
      if (t !== null) {
        if (fromTs !== null && t < fromTs) return false;
        if (toTs !== null && t > toTs) return false;
      }
      return true;
    });
  }, [homes, fromTs, toTs, minPriceNum]);

  function applyFilters() {
    setAppliedFilters({ dateFrom, dateTo, minPrice });
  }

  const sortedHomes = useMemo(() => {
    const indexed = filteredHomes.map((h, i) => ({ h, i: homes.indexOf(h) }));
    indexed.sort((a, b) => {
      const av = a.h[sortKey];
      const bv = b.h[sortKey];
      const aE = av === undefined || av === null || av === "";
      const bE = bv === undefined || bv === null || bv === "";
      if (aE && bE) return 0;
      if (aE) return 1;
      if (bE) return -1;
      let cmp = 0;
      if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
      else if (sortKey === "soldDate") {
        const at = Date.parse(String(av));
        const bt = Date.parse(String(bv));
        cmp = (isNaN(at) ? 0 : at) - (isNaN(bt) ? 0 : bt);
      } else cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return indexed;
  }, [filteredHomes, homes, sortKey, sortDir]);

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      setSortDir(k === "price" || k === "soldDate" || k === "sqft" ? "desc" : "asc");
    }
  }

  async function onSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!/^\d{5}$/.test(zipcode)) {
      toast.error("Enter a 5-digit zip code");
      return;
    }
    setLoading(true);
    try {
      const password = getStoredPassword();
      if (!password) throw new Error("Enter the tool password again to search.");
      const res = await searchFn({ data: { zipcode, radius, days, password } });
      setHomes(res.homes);
      setSourceUrl(res.sourceUrl);
      setSourceName(res.source);

      setSearched(true);
      setSelected(new Set(res.homes.map((_, i) => i)));
      toast.success(`Found ${res.homes.length} homes`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Search failed");
    } finally {
      setLoading(false);
    }
  }

  function toggleRow(i: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  const visibleIndices = useMemo(() => new Set(sortedHomes.map(({ i }) => i)), [sortedHomes]);
  const allSelected = visibleIndices.size > 0 && [...visibleIndices].every((i) => selected.has(i));
  function toggleAll() {
    if (allSelected) {
      const next = new Set(selected);
      visibleIndices.forEach((i) => next.delete(i));
      setSelected(next);
    } else {
      const next = new Set(selected);
      visibleIndices.forEach((i) => next.add(i));
      setSelected(next);
    }
  }

  const exportCount = [...selected].filter((i) => visibleIndices.has(i)).length;

  function onExport() {
    const rows = homes.filter((_, i) => selected.has(i) && visibleIndices.has(i));
    if (!rows.length) {
      toast.error("Select at least one row to export");
      return;
    }
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sold-homes-${zipcode}-${radius}mi.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function onPrintEnvelopes() {
    const rows = homes.filter((_, i) => selected.has(i) && visibleIndices.has(i));
    if (!rows.length) {
      toast.error("Select at least one row to print");
      return;
    }
    const logoAbs = customLogo || new URL(logoUrl, window.location.origin).href;
    const esc = (s: string) =>
      s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
    const ret = returnAddr;
    const envelopes = rows
      .map((h) => {
        const cityLine = [h.city, h.state].filter(Boolean).join(", ") + (h.zip ? ` ${h.zip}` : "");
        return `
          <section class="env">
            <div class="ret">
              <img src="${esc(logoAbs)}" alt="" class="logo" />
              <div class="ret-text">
                <div>${esc(ret.name)}</div>
                ${ret.line1 ? `<div>${esc(ret.line1)}</div>` : ""}
                ${ret.line2 ? `<div>${esc(ret.line2)}</div>` : ""}
              </div>
            </div>
            <div class="to">
              <div class="name">Current Homeowner</div>
              <div>${esc(h.address || "")}</div>
              <div>${esc(cityLine)}</div>
            </div>

          </section>`;
      })
      .join("");

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Envelopes</title>
<style>
  @page { size: 9.5in 4.125in landscape; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; color: #000; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .env {
    width: 9.5in; height: 4.1in; position: relative;
    padding: 0.3in; overflow: hidden;
    break-inside: avoid; page-break-inside: avoid;
  }
  .env + .env { break-before: page; page-break-before: always; }
  .ret { display: flex; align-items: flex-start; gap: 0.12in; font-size: 9pt; line-height: 1.25; max-width: 4in; }
  .logo { height: 0.55in; max-width: 1.4in; width: auto; object-fit: contain; flex-shrink: 0; }
  .ret-text { padding-top: 2pt; }
  .ret-text div { white-space: nowrap; }
  .to {
    position: absolute; left: 3.9in; top: 1.9in;
    font-size: 12pt; line-height: 1.35;
    width: 5.2in;
  }
  .to .name { font-weight: bold; }
  .to div { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  @media screen {
    body { background: #eee; padding: 20px; }
    .env { background: #fff; margin: 0 auto 16px; box-shadow: 0 2px 10px rgba(0,0,0,.15); outline: 1px dashed #bbb; }
    .toolbar { max-width: 9.5in; margin: 0 auto 12px; text-align: right; font: 13px Arial, sans-serif; color: #333; }
    .toolbar button { padding: 8px 16px; font-size: 14px; cursor: pointer; margin-left: 10px; }
  }
  @media print { .toolbar { display: none; } body { background: #fff; padding: 0; } .env { margin: 0; box-shadow: none; outline: none; } }
</style></head><body>

<div class="toolbar"><span>In the print dialog choose paper size <b>Envelope #10 (9.5 x 4.125 in)</b>, landscape, margins <b>None</b>, and scale <b>100%</b>.</span><button onclick="window.print()">Print</button></div>
${envelopes}
<script>window.addEventListener('load',()=>setTimeout(()=>window.print(),400));</script>
</body></html>`;


    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Popup blocked — allow popups to print envelopes");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  }

  async function loadLogoPngBytes(src: string): Promise<Uint8Array | null> {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("logo load failed"));
        img.src = src;
      });
      const canvas = document.createElement("canvas");
      const maxW = 800;
      const scale = Math.min(1, maxW / img.width);
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/png");
      const b64 = dataUrl.split(",")[1] ?? "";
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return bytes;
    } catch {
      return null;
    }
  }

  async function onExportPdf() {
    const rows = homes.filter((_, i) => selected.has(i) && visibleIndices.has(i));
    if (!rows.length) {
      toast.error("Select at least one row to export");
      return;
    }
    try {
      const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
      const pdfDoc = await PDFDocument.create();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      const logoSrc = customLogo || new URL(logoUrl, window.location.origin).href;
      const logoBytes = await loadLogoPngBytes(logoSrc);
      const logoImg = logoBytes ? await pdfDoc.embedPng(logoBytes) : null;

      // #10 envelope: 9.5" x 4.125" (pt = in * 72)
      const W = 9.5 * 72;
      const H = 4.125 * 72;
      const margin = 0.3 * 72;
      const ret = returnAddr;

      for (const h of rows) {
        const page = pdfDoc.addPage([W, H]);

        // Return address (top-left). Cap logo by both height and width.
        let logoW = 0;
        const maxLogoH = 0.55 * 72;
        const maxLogoW = 1.4 * 72;
        let logoH = maxLogoH;
        if (logoImg) {
          const ratio = logoImg.width / logoImg.height;
          logoW = logoH * ratio;
          if (logoW > maxLogoW) {
            logoW = maxLogoW;
            logoH = maxLogoW / ratio;
          }
          page.drawImage(logoImg, {
            x: margin,
            y: H - margin - logoH,
            width: logoW,
            height: logoH,
          });
        }
        const retX = margin + (logoW ? logoW + 0.12 * 72 : 0);
        const retTop = H - margin - 9;
        const retLines = [ret.name, ret.line1, ret.line2].filter(Boolean) as string[];
        retLines.forEach((line, i) => {
          page.drawText(line, {
            x: retX,
            y: retTop - i * 12,
            size: 9,
            font,
            color: rgb(0, 0, 0),
          });
        });

        // Recipient block — USPS-standard position for #10
        const toX = 3.9 * 72;
        const toY = H - 1.9 * 72 - 12;

        const cityLine =
          [h.city, h.state].filter(Boolean).join(", ") + (h.zip ? ` ${h.zip}` : "");
        const toLines = ["Current Homeowner", h.address || "", cityLine];
        toLines.forEach((line, i) => {
          page.drawText(line, {
            x: toX,
            y: toY - i * 17,
            size: 12,
            font: i === 0 ? fontBold : font,
            color: rgb(0, 0, 0),
          });
        });
      }


      const bytes = await pdfDoc.save();
      const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `envelopes-${zipcode}-${radius}mi.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`PDF ready (${rows.length} envelope${rows.length === 1 ? "" : "s"})`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "PDF export failed");
    }
  }

  const SortHead = ({ k, label, className }: { k: SortKey; label: string; className?: string }) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className="inline-flex items-center gap-1 hover:text-foreground"
      >
        {label}
        {sortKey === k && (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </TableHead>
  );

  return (
    <main className="container mx-auto px-6 py-10">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Recently Sold Homes Search</h1>
        <p className="mt-2 text-muted-foreground">
          Enter a zip code and radius to find recently sold homes. Select rows and export as CSV.
        </p>

        <Card className="mt-6 p-6">
          <form onSubmit={onSearch} className="grid gap-4 md:grid-cols-[1fr_1fr_1fr_auto] items-end">
            <div>
              <Label htmlFor="zip">Zip code</Label>
              <Input
                id="zip"
                inputMode="numeric"
                maxLength={5}
                placeholder="80202"
                value={zipcode}
                onChange={(e) => setZipcode(e.target.value.replace(/\D/g, "").slice(0, 5))}
                required
              />
            </div>
            <div>
              <Label htmlFor="radius">Radius (miles)</Label>
              <select
                id="radius"
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm md:text-sm"
              >
                {RADIUS_OPTIONS.map((r) => (
                  <option key={r} value={r}>{r} mile{r === 1 ? "" : "s"}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="days">Sold within</Label>
              <select
                id="days"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm md:text-sm"
              >
                {[30, 60, 90, 180, 365].map((d) => (
                  <option key={d} value={d}>
                    Last {d} days
                  </option>
                ))}
              </select>
            </div>

            <Button type="submit" disabled={loading} className="h-9">
              {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Searching…</>) : (<><Search className="h-4 w-4" /> Search</>)}
            </Button>
          </form>
        </Card>

        {searched && (
          <div className="mt-8">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label htmlFor="date-from" className="text-xs text-muted-foreground">Sold from</Label>
                  <Input
                    id="date-from"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="h-9 w-40"
                  />
                </div>
                <div>
                  <Label htmlFor="date-to" className="text-xs text-muted-foreground">Sold to</Label>
                  <Input
                    id="date-to"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="h-9 w-40"
                  />
                </div>
                <div>
                  <Label htmlFor="min-price" className="text-xs text-muted-foreground">Min price</Label>
                  <Input
                    id="min-price"
                    type="number" onFocus={(e)=>(e.target as HTMLInputElement).select()}
                    placeholder="0"
                    min="0"
                    step="1000"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    className="h-9 w-40"
                  />
                </div>
                <Button type="button" onClick={applyFilters} className="h-9">
                  <Filter className="h-4 w-4" /> Filter
                </Button>
                <div className="text-sm text-muted-foreground pb-2">
                  {selected.size} of {filteredHomes.length} selected
                  {filteredHomes.length !== homes.length && ` · ${homes.length} total`}
                  {sourceName && ` · ${sourceName}`}

                  {sourceUrl && (
                    <>
                      {" · "}
                      <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline hover:text-foreground">
                        source <ExternalLink className="h-3 w-3" />
                      </a>
                    </>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={onExport} disabled={exportCount === 0} variant="outline" className="h-9">
                  <Download className="h-4 w-4" /> Export CSV ({exportCount})
                </Button>
                <Button onClick={onPrintEnvelopes} disabled={exportCount === 0} variant="outline" className="h-9">
                  <Printer className="h-4 w-4" /> Print envelopes ({exportCount})
                </Button>
                <Button onClick={onExportPdf} disabled={exportCount === 0} variant="outline" className="h-9">
                  <FileText className="h-4 w-4" /> Envelope PDF ({exportCount})
                </Button>
              </div>
            </div>

            {exportCount > 0 && (
              <Card className="p-4 mb-3">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="text-xs font-medium text-muted-foreground">
                    Return address (used on printed #10 envelopes)
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {savedNotice && <span className="text-xs text-muted-foreground">{savedNotice}</span>}
                    <Button type="button" size="sm" variant="outline" onClick={saveReturnAddress}>
                      Save as default
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-border">
                  <Label htmlFor="preset" className="text-xs text-muted-foreground">Preset</Label>
                  <select
                    id="preset"
                    value={activePresetId}
                    onChange={(e) => selectPreset(e.target.value)}
                    className="flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm min-w-[180px]"
                  >
                    <option value="">— None —</option>
                    {presets.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <Button type="button" size="sm" variant="outline" onClick={saveAsPreset}>
                    Save as new preset
                  </Button>
                  {activePresetId && (
                    <>
                      <Button type="button" size="sm" variant="outline" onClick={updateCurrentPreset}>
                        Update preset
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={deleteCurrentPreset}>
                        Delete
                      </Button>
                    </>
                  )}
                </div>
                <div className="grid gap-2 md:grid-cols-3">
                  <Input
                    placeholder="Company name"
                    value={returnAddr.name}
                    onChange={(e) => setReturnAddr((r) => ({ ...r, name: e.target.value }))}
                  />
                  <Input
                    placeholder="Street address"
                    value={returnAddr.line1}
                    onChange={(e) => setReturnAddr((r) => ({ ...r, line1: e.target.value }))}
                  />
                  <Input
                    placeholder="City, State ZIP"
                    value={returnAddr.line2}
                    onChange={(e) => setReturnAddr((r) => ({ ...r, line2: e.target.value }))}
                  />
                </div>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <img
                      src={customLogo || logoUrl}
                      alt="Logo preview"
                      className="h-10 w-auto object-contain border border-border rounded bg-white p-1"
                    />
                    <span className="text-xs text-muted-foreground">
                      {customLogo ? "Custom logo" : "Default logo"}
                    </span>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={onLogoUpload}
                      className="block text-xs file:mr-2 file:rounded-md file:border file:border-border file:bg-background file:px-3 file:py-1 file:text-xs file:font-medium hover:file:bg-accent"
                    />
                  </label>
                  {customLogo && (
                    <Button type="button" size="sm" variant="ghost" onClick={resetLogo}>
                      Use default logo
                    </Button>
                  )}
                </div>
              </Card>
            )}

            {homes.length === 0 ? (
              <Card className="p-8 text-center text-muted-foreground">
                No sold homes found. Try increasing the radius.
              </Card>
            ) : (
              <Card className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" />
                      </TableHead>
                      <SortHead k="address" label="Address" />
                      <SortHead k="city" label="City" />
                      <SortHead k="price" label="Price" />
                      <SortHead k="soldDate" label="Sold" />
                      <SortHead k="beds" label="Beds" />
                      <SortHead k="baths" label="Baths" />
                      <SortHead k="sqft" label="Sqft" />
                      <SortHead k="yearBuilt" label="Year" />
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedHomes.map(({ h, i }) => (
                      <TableRow key={i} data-state={selected.has(i) ? "selected" : undefined}>
                        <TableCell>
                          <Checkbox
                            checked={selected.has(i)}
                            onCheckedChange={() => toggleRow(i)}
                            aria-label={`Select ${h.address}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{h.address}</TableCell>
                        <TableCell>
                          {[h.city, h.state].filter(Boolean).join(", ")}
                          {h.zip ? ` ${h.zip}` : ""}
                        </TableCell>
                        <TableCell>{fmtMoney(h.price)}</TableCell>
                        <TableCell>{h.soldDate || ""}</TableCell>
                        <TableCell>{h.beds ?? ""}</TableCell>
                        <TableCell>{h.baths ?? ""}</TableCell>
                        <TableCell>{h.sqft?.toLocaleString() ?? ""}</TableCell>
                        <TableCell>{h.yearBuilt ?? ""}</TableCell>
                        <TableCell>
                          {h.url && (
                            <a href={h.url} target="_blank" rel="noopener noreferrer" aria-label="Open listing">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
